<template>
  <div class="flex flex-col gap-y-2">
    <div v-for="criteria in passwordCriteria" :key="criteria.id">
      <div class="flex items-center gap-x-1">
        <CheckedIcon v-if="criteria.passed" class="size-4 text-good" />
        <ErrorIcon v-else class="size-4 text-error" />
        <p class="text-sm">{{ criteria.label }}</p>
      </div>
    </div>
    <p class="text-sm font-semibold">Password Strength</p>
    <div id="password-strength-bar" class="relative h-5 rounded-full bg-s-400">
      <div
        id="progress-bar"
        :style="{
          width: progresswidth,
          backgroundColor: progresscolor,
        }"
        class="h-full rounded-full"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
/** assets */
import CheckedIcon from '@/assets/icons/password-check.svg'
import ErrorIcon from '@/assets/icons/password-error.svg'

/** constants */
import { PASSWORD_CRITERIA, BAR_COLORS } from '~/src/constants/auth'

/** props */
const props = defineProps<{
  confirmationValue?: string | null
  inputValue?: string | null
}>()

const emit = defineEmits(['on-valid'])

const ONE_HUNDRED = 100
/** methods */

const passwordCriteria = computed(() => {
  if (!props.inputValue) return PASSWORD_CRITERIA

  return PASSWORD_CRITERIA.map((criteria) => {
    if (!criteria.rule) return criteria
    const passed = criteria.rule(props.inputValue, props.confirmationValue)
    return { ...criteria, passed }
  })
})

const criteriaPercentage = computed(() => {
  const percentage = passwordCriteria.value.filter(
    (criteria) => criteria.passed,
  ).length
  return (percentage / passwordCriteria.value.length) * ONE_HUNDRED
})

const isValid = computed(() => {
  const valid = criteriaPercentage.value === ONE_HUNDRED
  emit('on-valid', valid)
  return valid
})

const progresswidth = computed(() => criteriaPercentage.value + '%')

const progresscolor = computed(() => {
  if (isValid.value) return BAR_COLORS.success
  if (criteriaPercentage.value < 60) return BAR_COLORS.error
  return BAR_COLORS.warning
})
</script>

<style>
#progress-bar {
  transition: all 0.3s;
}
</style>
